/**
 * layoutStyles.js — theme tokens + section-visibility presets registry.
 *
 * ⚠️ This file is NOT the source of truth for layout structure.
 *
 * The actual visual structure for each game page is defined by the per-game
 * `*Style*.jsx` files in `src/pages/layouts/` (e.g. `Classic5050StyleStandard`,
 * `SquareGameStyleCharity`). Those files own the JSX tree, hero block,
 * sidebar/main composition, and section ordering for their layout.
 *
 * What `LAYOUT_STYLES` provides:
 *   - `theme`               coarse light/dark/gradient hint consumed by
 *                           `GameHomePage` + content pages for top-level
 *                           wrapper styling
 *   - `gridLayout`          column-split hint consumed by
 *                           `getGridLayoutClasses` in `GameHomePage`
 *   - `show*` boolean flags section-visibility hints consumed by
 *                           `GameHomePage` (showHero, showCarousel,
 *                           showBonusDraws, etc.)
 *   - `id`/`name`           identity / admin-selector metadata
 *
 * What it does NOT (any longer) carry:
 *   - `countdownVariant`    — countdown variant lives on `StandardHeroBlock`
 *                             theme tokens, not on layout config
 *   - `sectionOrder`        — actual ordering is defined by `useSectionVisibility`
 *                             + per-layout JSX, not config
 *   - `features.*`          — every layout file owns its own feature flags
 *
 * If you find yourself wanting to add a new structural field here, push it
 * down into the layout file instead. This registry is intentionally lean.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Game-page layout style presets
// Consumed by: src/pages/GameHomePage.jsx (theme + show* flags + gridLayout)
// ─────────────────────────────────────────────────────────────────────────────

export const LAYOUT_STYLES = {
  standard: {
    id: 'standard',
    name: 'Standard',
    showHero: false,
    showCharityHeroStrip: true,
    showCountdown: true,
    showMetricsCard: true,
    showCarousel: true,
    showLiveDrawCard: true,
    showBonusDraws: true,
    showCompactWinners: true,
    showCharitySidebar: true,
    showPartnerBanner: true,
    showWinnersBanner: true,
    showHowItWorks: true,
    showLastDrawInfo: true,
    gridLayout: '2/3-1/3',
    theme: 'light',
  },

  style1: {
    id: 'style1',
    name: 'Hero Carousel',
    showHero: true,
    showCharityHeroStrip: false,
    showCountdown: true,
    showMetricsCard: false,
    showCarousel: true,
    showLiveDrawCard: true,
    showBonusDraws: true,
    showCompactWinners: false,
    showCharitySidebar: false,
    showPartnerBanner: true,
    showWinnersBanner: true,
    showHowItWorks: false,
    showLastDrawInfo: false,
    showSplitPotCharity: true,
    gridLayout: '1/3-2/3',
    theme: 'light',
  },

  style2: {
    id: 'style2',
    name: 'Premium Showcase',
    showHero: true,
    showCharityHeroStrip: false,
    showCountdown: true,
    showMetricsCard: true,
    showCarousel: false,
    showLiveDrawCard: false,
    showBonusDraws: true,
    showCompactWinners: false,
    showCharitySidebar: false,
    showPartnerBanner: true,
    showWinnersBanner: true,
    showHowItWorks: false,
    showLastDrawInfo: false,
    showSplitPotCharity: true,
    showTopPartners: true,
    gridLayout: 'full-width',
    theme: 'gradient',
  },

  style3: {
    id: 'style3',
    name: 'Feature Focus',
    showHero: true,
    showCharityHeroStrip: false,
    showCountdown: true,
    showMetricsCard: false,
    showCarousel: false,
    showLiveDrawCard: true,
    showBonusDraws: true,
    showCompactWinners: false,
    showCharitySidebar: false,
    showPartnerBanner: true,
    showWinnersBanner: true,
    showHowItWorks: true,
    showLastDrawInfo: false,
    showSplitPotCharity: true,
    showCharityBanner: true,
    showFeatureCards: true,
    gridLayout: '2/3-1/3',
    theme: 'light',
  },

  style4: {
    id: 'style4',
    name: 'Game Show',
    showHero: true,
    showCharityHeroStrip: false,
    showCountdown: true,
    showMetricsCard: true,
    showCarousel: false,
    showLiveDrawCard: false,
    showBonusDraws: true,
    showCompactWinners: false,
    showCharitySidebar: false,
    showPartnerBanner: true,
    showWinnersBanner: true,
    showHowItWorks: false,
    showLastDrawInfo: false,
    showSplitPotCharity: true,
    showLiveActivity: true,
    showQuickPicks: true,
    gridLayout: 'full-width',
    theme: 'dark',
  },

  style5: {
    id: 'style5',
    name: 'Spotlight',
    showHero: true,
    showCharityHeroStrip: false,
    showCountdown: true,
    showMetricsCard: true,
    showCarousel: false,
    showLiveDrawCard: false,
    showBonusDraws: true,
    showCompactWinners: false,
    showCharitySidebar: false,
    showPartnerBanner: true,
    showWinnersBanner: true,
    showHowItWorks: false,
    showLastDrawInfo: false,
    showSplitPotCharity: true,
    showTicketTiers: true,
    gridLayout: 'full-width',
    theme: 'dark',
  },

  premium: {
    id: 'premium',
    name: 'Premium',
    showHero: true,
    showCharityHeroStrip: false,
    showCountdown: true,
    showMetricsCard: false,
    showCarousel: false,
    showLiveDrawCard: false,
    showBonusDraws: true,
    showCompactWinners: true,
    showCharitySidebar: false,
    showPartnerBanner: true,
    showWinnersBanner: true,
    showHowItWorks: true,
    showLastDrawInfo: false,
    showSplitPotCharity: true,
    gridLayout: 'full-width',
    theme: 'light',
  },

  'premium-plus': {
    id: 'premium-plus',
    name: 'Premium +',
    showHero: true,
    showCharityHeroStrip: false,
    showCountdown: true,
    showMetricsCard: false,
    showCarousel: false,
    showLiveDrawCard: false,
    showBonusDraws: true,
    showCompactWinners: true,
    showCharitySidebar: false,
    showPartnerBanner: true,
    showWinnersBanner: true,
    showHowItWorks: true,
    showLastDrawInfo: false,
    showSplitPotCharity: true,
    showTopPartners: true,
    gridLayout: 'full-width',
    theme: 'light',
  },

  fluid: {
    id: 'fluid',
    name: 'Fluid Stack',
    showHero: false,
    showCharityHeroStrip: false,
    showCountdown: true,
    showMetricsCard: false,
    showCarousel: false,
    showLiveDrawCard: true,
    showBonusDraws: true,
    showCompactWinners: true,
    showCharitySidebar: false,
    showPartnerBanner: true,
    showWinnersBanner: true,
    showHowItWorks: true,
    showLastDrawInfo: false,
    showSplitPotCharity: true,
    gridLayout: 'full-width',
    theme: 'light',
  },

  cinematic: {
    id: 'cinematic',
    name: 'Cinematic',
    showHero: true,
    showCharityHeroStrip: false,
    showCountdown: true,
    showMetricsCard: false,
    showCarousel: false,
    showLiveDrawCard: false,
    showBonusDraws: true,
    showCompactWinners: false,
    showCharitySidebar: false,
    showPartnerBanner: false,
    showWinnersBanner: true,
    showHowItWorks: false,
    showLastDrawInfo: false,
    showSplitPotCharity: false,
    gridLayout: 'full-width',
    theme: 'dark',
  },
};

/**
 * Get the configuration for a specific layout style.
 * Falls back to `standard` when the key is missing or unknown.
 *
 * Note: many game style keys (e.g. `v2`, `deckRunner`, `liveBoard`, `gameday`,
 * `charity`) intentionally don't have entries here — those layouts don't
 * consult this registry and rely on their own internal defaults. The fallback
 * to `standard` keeps `GameHomePage`'s top-level theme/visibility hints sane
 * for those keys.
 *
 * @param {string} layoutStyle
 * @returns {object} layout style config
 */
export function getLayoutStyleConfig(layoutStyle) {
  if (!layoutStyle) {
    return LAYOUT_STYLES.standard;
  }

  // Normalize the style key (handle both 'style1' and '1' formats)
  const normalizedKey = layoutStyle.toLowerCase().replace(/^style/, '');

  if (normalizedKey === '' || normalizedKey === 'standard') {
    return LAYOUT_STYLES.standard;
  }

  const styleKey = `style${normalizedKey}`;
  return LAYOUT_STYLES[styleKey] || LAYOUT_STYLES[layoutStyle] || LAYOUT_STYLES.standard;
}

/**
 * Get the grid-layout CSS classes for a style.
 * @param {string} layoutStyle
 * @returns {{main: string, sidebar: string|null, container: string}}
 */
export function getGridLayoutClasses(layoutStyle) {
  const config = getLayoutStyleConfig(layoutStyle);

  switch (config.gridLayout) {
    case '2/3-1/3':
      return {
        main: 'lg:col-span-2',
        sidebar: 'lg:col-span-1',
        container: 'grid grid-cols-1 lg:grid-cols-3 gap-8',
      };
    case '1/3-2/3':
      return {
        main: 'lg:col-span-2 order-last lg:order-first',
        sidebar: 'lg:col-span-1 order-first lg:order-last',
        container: 'grid grid-cols-1 lg:grid-cols-3 gap-8',
      };
    case 'full-width':
      return {
        main: 'col-span-full',
        sidebar: null,
        container: 'flex flex-col',
      };
    default:
      return {
        main: 'lg:col-span-2',
        sidebar: 'lg:col-span-1',
        container: 'grid grid-cols-1 lg:grid-cols-3 gap-8',
      };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// CONTENT_PAGE_LAYOUTS — lightweight presets for content pages
// (About, HowToPlay, Winners, Draws).
//
// These pages don't need game-specific concerns (countdown, jackpot, metrics).
// Only `theme` is read at render time; `description` is surfaced in the admin
// style selector via `getContentPageLayoutOptions`.
// ─────────────────────────────────────────────────────────────────────────────

export const CONTENT_PAGE_LAYOUTS = {
  standard: {
    id: 'standard',
    name: 'Standard',
    description: 'Clean single-column content layout',
    theme: 'app',
  },

  hero: {
    id: 'hero',
    name: 'Hero Focus',
    description: 'Large hero section with prominent visual header',
    theme: 'dark',
  },

  minimal: {
    id: 'minimal',
    name: 'Minimal',
    description: 'Typography-first, minimal visuals',
    theme: 'dark',
  },

  gallery: {
    id: 'gallery',
    name: 'Gallery',
    description: 'Image-rich grid layout for visual content',
    theme: 'app',
  },
};

/**
 * Get the configuration for a specific content page layout style.
 * @param {string} style
 * @returns {object} content page layout config
 */
export function getContentPageLayoutConfig(style) {
  return CONTENT_PAGE_LAYOUTS[style] || CONTENT_PAGE_LAYOUTS.standard;
}

/**
 * Return Tailwind classes for a content-page wrapper based on its layout theme.
 *
 * - `'app'`      — transparent; inherits the app shell background set via
 *                   --app-bg-light / --app-bg-dark. Uses Tailwind `dark:` for text.
 * - `'dark'`     — explicit dark surface.
 * - `'gradient'` — legacy purple/red gradient (kept for backward compat).
 * - anything else falls through to `'app'` behavior.
 *
 * @param {string} theme - one of the `theme` values from CONTENT_PAGE_LAYOUTS
 * @returns {string} Tailwind class string
 */
export function getContentPageThemeClass(theme) {
  switch (theme) {
    case 'dark':
      return 'bg-gray-900 text-white';
    case 'gradient':
      return 'dark bg-gradient-to-br from-purple-900 via-pink-900 to-red-900 text-white';
    case 'app':
    default:
      return 'text-gray-900 dark:text-white';
  }
}

/**
 * Get content page layout style options formatted for select/dropdown components.
 * @returns {Array<{id: string, name: string, description: string}>}
 */
export function getContentPageLayoutOptions() {
  return Object.entries(CONTENT_PAGE_LAYOUTS).map(([id, cfg]) => ({
    id,
    name: cfg.name,
    description: cfg.description,
  }));
}

/**
 * Get the visible sections for a content page layout in order.
 * Content pages don't currently override section order — sort by section_order.
 *
 * @param {string} _layoutStyle - reserved for future per-layout ordering
 * @param {Array} sections - sections from the page
 * @returns {Array} filtered + sorted sections
 */
export function getContentPageVisibleSections(_layoutStyle, sections = []) {
  return sections
    .filter((s) => s.is_visible)
    .sort((a, b) => a.section_order - b.section_order);
}

export default LAYOUT_STYLES;
